<template>
  <div>
    <ValidationObserver ref="formModalValidate">
      <b-row>
        <b-col cols="12" md="6">
          <ValidationProvider name="period_id" rules="required" v-slot="{ errors }">
            <b-form-group :label="$t('period')">
              <payment-periods-selectbox
                v-model.lazy="formData.period_id"
              />
              <b-form-invalid-feedback v-if="errors[0]" v-html="errors[0]" />
            </b-form-group>
          </ValidationProvider>
        </b-col>

        <b-col cols="12" md="6">
          <ValidationProvider name="product_id" rules="required" v-slot="{ errors }">
            <b-form-group :label="$t('product')">
              <payment-products-selectbox
                type="number"
                v-model="formData.product_id"
                :state="errors[0] ? false : null"
                :validate-error="errors[0]"
              />
            </b-form-group>
          </ValidationProvider>
        </b-col>

        <b-col cols="12" md="6">
          <ValidationProvider name="invoice_date" rules="required" v-slot="{ errors }">
            <b-form-group :label="$t('invoice_date')">
              <select-date v-model="formData.invoice_date" :validation-error="errors[0]" />
            </b-form-group>
          </ValidationProvider>
        </b-col>

        <b-col cols="12" md="6">
          <ValidationProvider name="student_numbers" rules="required" v-slot="{ errors }">
            <b-form-group :label="$t('student_numbers')">
              <lined-textarea
                v-model="formData.student_numbers"
                :nowrap="false"
                :disabled="false"
                :styles="{ height: '10em', resize: 'both' }"
                :validateError="errors[0]"
              />
              <div class="invalid-feedback d-block" v-if="errors[0]" v-html="errors[0]" />
            </b-form-group>
          </ValidationProvider>
        </b-col>

        <b-col cols="12" md="6">
          <ValidationProvider name="installments_count" rules="required" v-slot="{ errors }">
            <b-form-group :label="$t('installments')">
              <multi-selectbox
                v-model="formData.installments_count"
                :options="installmentOptions"
                :validateError="errors[0]"
                @input="onInstallmentCountChange"
              />
            </b-form-group>
          </ValidationProvider>
        </b-col>

        <b-col cols="12">
          <div
            v-for="(item, idx) in formData.installments"
            :key="idx"
            class="border rounded p-3 mb-2"
          >
            <div class="d-flex flex-wrap">
              <div class="mr-3 mb-2">
                <ValidationProvider
                  :name="`installments.${idx}.amount`"
                  rules="required|numeric"
                  v-slot="{ errors }"
                >
                  <b-form-group :label="$t('amount')">
                    <b-form-input
                      type="number"
                      min="0"
                      step="0.01"
                      v-model.number="item.amount"
                      :state="errors[0] ? false : null"
                    />
                    <b-form-invalid-feedback v-if="errors[0]">{{ errors[0] }}</b-form-invalid-feedback>
                  </b-form-group>
                </ValidationProvider>
              </div>

              <div class="mb-2">
                <ValidationProvider
                  :name="`installments.${idx}.due_date`"
                  rules="required"
                  v-slot="{ errors }"
                >
                  <b-form-group :label="$t('due_date')">
                    <select-date
                      v-model="item.due_date"
                      :validation-error="errors[0]"
                    />
                  </b-form-group>
                </ValidationProvider>
              </div>
            </div>
          </div>
        </b-col>
      </b-row>

      <div class="d-flex justify-content-center mt-2">
        <b-button variant="primary" @click="storeForm">
          {{ $t('save') }}
        </b-button>
      </div>
    </ValidationObserver>
  </div>
</template>

<script>
import { ValidationProvider, ValidationObserver } from "vee-validate";
import PaymentPlanService from "@/services/PaymentPlanService";
import PaymentPeriodsSelectbox from "@/components/interactive-fields/PaymentPeriodsSelectbox.vue";
import PaymentProductsSelectbox from "@/components/interactive-fields/PaymentProductsSelectbox.vue";
import LinedTextarea from "@/components/elements/LinedTextarea.vue";
import MultiSelectbox from "@/components/interactive-fields/MultiSelectbox.vue";
import moment from 'moment';

export default {
  components: {
    ValidationProvider,
    ValidationObserver,
    PaymentPeriodsSelectbox,
    PaymentProductsSelectbox,
    LinedTextarea,
    MultiSelectbox,
  },
  data() {
    return {
        formData: {
            period_id: null,
            product_id: null,
            invoice_date: null,
            student_numbers: "",
            installments_count: null,
            installments: [],
        },
        installmentOptions:[
            {value:'2', text:'2'},
            {value:'3', text:'3'},
            {value:'4', text:'4'},
            {value:'5', text:'5'},
            {value:'6', text:'6'},
            {value:'7', text:'7'},
            {value:'8', text:'8'},
            {value:'9', text:'9'},
            {value:'10', text:'10'},
            {value:'11', text:'11'},
            {value:'12', text:'12'},
        ],
        formLoading: false,
    };
  },
  methods: {
    onInstallmentCountChange(count) {
      const n = Number(count || 0);
      const next = Array.from({ length: n }, (_, i) => ({
        amount: this.formData.installments?.[i]?.amount ?? null,
        due_date: this.formData.installments?.[i]?.due_date ?? null,
      }));
      this.formData.installments = next;
    },

    async storeForm() {
      const isValid = await this.$refs.formModalValidate.validate();
      if (!isValid) return;

      this.formLoading = true;
      const payload = { ...this.formData };

      if (payload.student_numbers === "") {
        delete payload.student_numbers;
      } else {
        payload.student_numbers = payload.student_numbers
          .split(/\n/)
          .map((s) => s.trim())
          .filter(Boolean);
      }

      payload.installments = (payload.installments || []).map((it) => ({
        amount: it?.amount != null ? Number(it.amount) : null,
        due_date: it?.due_date ? moment(it.due_date).format("MM/DD/YYYY") : null,
      }));

      delete payload.installments_count;

      PaymentPlanService.createByData(payload)
        .then((response) => {
          this.$emit("createFormSuccess", true);
          this.$toast.success(this.$t("api." + response.data.message));
        })
        .catch((e) => {
          this.showErrors(e, this.$refs.formModalValidate);
        })
        .finally(() => {
          this.formLoading = false;
        });
    },
  },
};
</script>